<template>
  <div class="mobile-card">
    <el-card shadow="hover" class="order-card">
      <template #header>
        <div class="card-header">
          <el-checkbox v-if="showCheckbox" v-model="isSelected" @change="handleSelect" class="card-checkbox"></el-checkbox>
          <span class="card-title">{{ title || '订单号: ' + (item.orderNumber || '无') }}</span>
          <slot name="header-tag">
            <el-tag :type="getStatusType(item.status)" size="small">{{ getStatusText(item.status) }}</el-tag>
          </slot>
        </div>
      </template>

      <div class="card-content">
        <div class="card-item" v-for="(field, index) in visibleFields" :key="index">
          <span class="card-label">{{ field.label }}:</span>
          <span class="card-value">{{ getFieldValue(field) }}</span>
        </div>
      </div>

      <div class="card-actions">
        <slot name="actions">
          <el-button size="small" @click="$emit('edit', item)">编辑</el-button>
          <el-button size="small" type="danger" @click="$emit('delete', item)">删除</el-button>
          <el-button size="small" type="primary" @click="$emit('details', item)">查看详情</el-button>
          <el-button size="small" type="info" @click="$emit('check-tracking', item)" v-if="item.trackingNumber" class="tracking-button">查询物流</el-button>
        </slot>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

// 定义props
const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    default: 0
  },
  currentPage: {
    type: Number,
    default: 1
  },
  pageSize: {
    type: Number,
    default: 10
  },
  title: {
    type: String,
    default: ''
  },
  fields: {
    type: Array,
    default: () => []
  },
  showCheckbox: {
    type: Boolean,
    default: true
  },
  initialSelected: {
    type: Boolean,
    default: false
  },
  orderType: {
    type: String,
    default: 'regular' // 'regular', 'fuxiangsan', 'bulk'
  }
});

// 事件
const emit = defineEmits(['select', 'edit', 'delete', 'details', 'check-tracking']);

// 响应式数据
const isSelected = ref(props.initialSelected || (props.item.selected === true));

// 计算属性
const visibleFields = computed(() => {
  if (props.fields && props.fields.length > 0) {
    return props.fields;
  }

  // 默认显示字段
  const defaultFields = [
    { label: '序号', field: 'displayIndex', formatter: () => props.index + 1 + (props.currentPage - 1) * props.pageSize },
    { label: '订单类型', field: 'orderType', default: props.orderType === 'regular' ? '超主点订单' : props.orderType === 'fuxiangsan' ? '其他订单' : '批量订单' },
    { label: '患者姓名', field: 'patientName', default: '未知' },
    { label: '验配单位', field: 'company', default: '无' },
    { label: '订单型号', field: 'lensModel', default: '无' },
    { label: '创建时间', field: 'createdAt', default: '无' }
  ];

  // 批量订单特殊字段
  if (props.orderType === 'bulk') {
    return [
      { label: '序号', field: 'displayIndex', formatter: () => props.index + 1 + (props.currentPage - 1) * props.pageSize },
      { label: '订单类型', field: 'orderType', default: '批量订单' },
      { label: '验配单位', field: 'company', default: '无' },
      { label: '订单型号', field: 'lensModel', default: '无' },
      { label: '数量', field: 'quantity', default: '0' },
    ];
  }

  return defaultFields;
});

// 方法
const getStatusType = (status) => {
  switch (status) {
    case 'Exported': return 'success';
    case 'Noexported': return 'warning';
    default: return 'info';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'Exported': return '已导出';
    case 'Noexported': return '未导出';
    default: return status || '未知';
  }
};

const getFieldValue = (field) => {
  if (field.formatter) {
    return field.formatter(props.item[field.field], props.item);
  }
  
  const value = props.item[field.field];
  return value !== null && value !== undefined ? value : field.default || '';
};

const handleSelect = () => {
  emit('select', { 
    item: props.item, 
    selected: isSelected.value, 
    orderType: props.orderType 
  });
};
</script>

<style scoped>
.mobile-card {
  width: 100%;
}

.order-card {
  background-color: #fff;
  margin-bottom: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
}

.order-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.card-checkbox {
  margin-right: 5px;
}

.card-title {
  font-size: 14px;
  color: #303133;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0 5px;
}

.card-content {
  padding: 8px 0;
}

.card-item {
  position: relative;
  padding: 8px 0;
  border-bottom: 1px dashed #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-item:last-child {
  border-bottom: none;
}

.card-label {
  color: #909399;
  font-size: 13px;
  min-width: 80px;
}

.card-value {
  color: #303133;
  font-size: 14px;
  text-align: right;
  flex-grow: 1;
  word-break: break-all;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #EBEEF5;
  flex-wrap: wrap;
}

.card-actions .el-button {
  margin: 0;
}

@media screen and (max-width: 768px) {
  .card-actions {
    background-color: #f9fafc;
    padding: 10px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-areas: 
      "edit delete details"
      "tracking tracking tracking";
    gap: 8px;
    border-top: 1px solid #ebeef5;
  }

  .card-actions .el-button {
    margin: 0;
    font-size: 12px;
    padding: 8px 0;
  }
  
  .card-actions .el-button:nth-child(1) {
    grid-area: edit;
  }
  
  .card-actions .el-button:nth-child(2) {
    grid-area: delete;
  }
  
  .card-actions .el-button:nth-child(3) {
    grid-area: details;
  }
  
  .card-actions .tracking-button {
    grid-area: tracking;
    margin-top: 8px;
  }
}
</style> 